import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  Inbox,
  Send,
  X,
  Mail,
  LogOut,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Star,
  ShieldAlert,
  Plus,
  Trash2,
  StickyNote,
  Folder,
  Image as ImageIcon,
  Sparkles,
} from 'lucide-react';
import { useUnreadInbox } from '../context/UnreadInboxContext';
import { useAuth } from '../context/AuthContext';
import { useMailFolders } from '../context/MailFoldersContext';
import { useMailDrag, useMailDropTarget } from '../context/MailDragContext';
import toast from 'react-hot-toast';

/** Randzone zum Scrollen beim Drag (px) – bewusst groß und klar sichtbar */
const DRAG_SCROLL_EDGE = 56;
/** Max. Scrollgeschwindigkeit in px/Frame (~60fps → ca. 180 px/s) */
const DRAG_SCROLL_MAX_PX = 3;
const DRAG_SCROLL_MIN_PX = 0.8;

const SidebarDropZone = ({
  dropKey,
  target,
  label,
  icon: Icon,
  color,
  depth = 0,
  parentLabel = null,
  nestedLeaf = false,
}) => {
  const { isOver, dropProps } = useMailDropTarget(dropKey, target, { enabled: true });
  const isNested = depth > 0;
  return (
    <button
      type="button"
      {...dropProps}
      className={`w-full flex items-stretch text-left transition-colors rounded-lg ${
        isOver
          ? 'bg-primary-100 dark:bg-primary-900/50 ring-1 ring-primary-400 dark:ring-primary-500 text-primary-900 dark:text-primary-100'
          : 'bg-neutral-50 dark:bg-neutral-800/70 text-neutral-800 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800'
      }`}
    >
      {isNested ? (
        <span
          className="flex-shrink-0 flex items-stretch self-stretch"
          style={{ width: `${12 + depth * 14}px` }}
          aria-hidden="true"
        >
          <span className="relative ml-auto w-3.5 self-stretch">
            <span className="absolute left-1 top-0 bottom-1/2 w-px bg-neutral-300 dark:bg-neutral-600" />
            <span className="absolute left-1 top-1/2 w-2.5 h-px bg-neutral-300 dark:bg-neutral-600" />
          </span>
        </span>
      ) : null}
      <span
        className={`min-w-0 flex-1 flex items-center gap-2.5 py-1.5 pr-2.5 ${
          isNested ? 'pl-0.5' : 'pl-2.5'
        }`}
      >
        {color ? (
          nestedLeaf ? (
            <Mail className="w-4 h-4 flex-shrink-0" style={{ color }} />
          ) : (
            <Folder className="w-4 h-4 flex-shrink-0" style={{ color }} fill={color} fillOpacity={0.2} />
          )
        ) : (
          <Icon className={`w-4 h-4 flex-shrink-0 ${isOver ? 'text-primary-600 dark:text-primary-400' : 'text-neutral-400'}`} />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium leading-tight">{label}</span>
          {parentLabel ? (
            <span className="block truncate text-[10px] font-normal text-neutral-500 dark:text-neutral-400 leading-tight mt-0.5">
              in {parentLabel}
            </span>
          ) : null}
        </span>
        {isOver ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400 flex-shrink-0">
            Ablegen
          </span>
        ) : null}
      </span>
    </button>
  );
};

const topNavItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/contacts', label: 'Kontakte', icon: Users },
  { to: '/templates', label: 'Templates', icon: FileText },
  { to: '/media', label: 'Medien', icon: ImageIcon },
  { to: '/ai-chat', label: 'KI-Chat', icon: Sparkles },
  { to: '/sticky-notes', label: 'Notizen', icon: StickyNote },
];

const formatFolderBadge = (n, { alwaysShow = false } = {}) => {
  const count = Math.max(0, Number(n) || 0);
  if (!alwaysShow && count <= 0) return null;
  if (count > 5000) return '5000+';
  return String(count);
};

const FolderCountBadge = ({ count, tone = 'primary', alwaysShow = false }) => {
  const label = formatFolderBadge(count, { alwaysShow });
  if (!label) return null;
  const toneClass =
    tone === 'danger'
      ? 'bg-danger-600 text-white'
      : tone === 'warning'
        ? 'bg-amber-500 text-white'
        : tone === 'spam'
          ? 'bg-orange-600 text-white'
        : tone === 'muted'
          ? 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200'
          : 'bg-primary-600 text-white';
  return (
    <span
      className={`ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums flex items-center justify-center flex-shrink-0 ${toneClass}`}
    >
      {label}
    </span>
  );
};

const inboxChildren = [
  { view: 'inbox', label: 'Empfangen', icon: Inbox },
  { view: 'important', label: 'Wichtig', icon: Star },
  { view: 'sent', label: 'Gesendet', icon: Send },
  { view: 'drafts', label: 'Entwürfe', icon: FileText },
  { view: 'spam', label: 'Spam', icon: ShieldAlert },
  { view: 'trash', label: 'Papierkorb', icon: Trash2 },
];

const CATEGORY_COLORS = [
  '#0f766e', '#0369a1', '#7c3aed', '#c2410c',
  '#be123c', '#15803d', '#a16207', '#475569',
];

const CATEGORY_EXIT_MS = 280;

/** Root = 0; darunter max. 3 Unterordner-Ebenen */
const MAX_FOLDER_DEPTH = 3;

const hexToRgb = (hex) => {
  const raw = String(hex || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return null;
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16),
  };
};

const withAlpha = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(15, 118, 110, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const formatMailboxBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = value >= 100 || unit === 0 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })} ${units[unit]}`;
};

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const { unreadCount, folderCounts, mailboxQuota } = useUnreadInbox();
  const { user, logout } = useAuth();
  const { flatFolders, createCategory, updateCategory, deleteCategory } = useMailFolders();
  const { isDragging, dragging } = useMailDrag();
  const location = useLocation();
  const navigate = useNavigate();
  const inboxActive = location.pathname.startsWith('/inbox');
  /** Im Postfach auf dem Desktop nur Icons — volle Breite bleibt auf Mobil und beim Ablegen. */
  const iconRail = inboxActive && !isDragging;
  const navItems = topNavItems;
  const [inboxOpen, setInboxOpen] = useState(inboxActive);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [createParentId, setCreateParentId] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState(CATEGORY_COLORS[0]);
  const [editingColorId, setEditingColorId] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [enteringCategoryIds, setEnteringCategoryIds] = useState(() => new Set());
  const [exitingCategoryIds, setExitingCategoryIds] = useState(() => new Set());
  const [collapsedFolderIds, setCollapsedFolderIds] = useState(() => new Set());
  const [dragScrollDir, setDragScrollDir] = useState(null); // 'up' | 'down' | null
  const [dragScrollHints, setDragScrollHints] = useState({ up: false, down: false });
  const savingCategoryRef = useRef(false);
  const dragScrollRef = useRef(null);
  const dragScrollDirRef = useRef(null);
  const dragScrollRafRef = useRef(0);
  const shellFooterRef = useRef(null);

  useLayoutEffect(() => {
    const el = shellFooterRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h > 0) {
        document.documentElement.style.setProperty('--odium-shell-footer-height', `${h}px`);
      }
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--odium-shell-footer-height');
    };
  }, [isDragging, mailboxQuota?.supported, mailboxQuota?.percent, user?.email]);

  const foldersById = useMemo(() => {
    const map = new Map();
    flatFolders.forEach((f) => map.set(String(f.id), f));
    return map;
  }, [flatFolders]);

  const visibleFolders = useMemo(() => {
    return flatFolders.filter((folder) => {
      let parentId = folder.parentId;
      while (parentId != null) {
        if (collapsedFolderIds.has(String(parentId))) return false;
        parentId = foldersById.get(String(parentId))?.parentId ?? null;
      }
      return true;
    });
  }, [flatFolders, collapsedFolderIds, foldersById]);

  useEffect(() => {
    if (inboxActive) setInboxOpen(true);
  }, [inboxActive]);

  useEffect(() => {
    if (isDragging) setSidebarOpen(true);
  }, [isDragging, setSidebarOpen]);

  // Langsames, kontrolliertes Scrollen in der Drag-Sidebar (statt Browser-Autosroll)
  useEffect(() => {
    if (!isDragging) {
      dragScrollDirRef.current = null;
      setDragScrollDir(null);
      setDragScrollHints({ up: false, down: false });
      if (dragScrollRafRef.current) {
        cancelAnimationFrame(dragScrollRafRef.current);
        dragScrollRafRef.current = 0;
      }
      return undefined;
    }

    const updateHints = () => {
      const el = dragScrollRef.current;
      if (!el) return;
      const max = el.scrollHeight - el.clientHeight;
      setDragScrollHints({
        up: el.scrollTop > 2,
        down: max > 2 && el.scrollTop < max - 2,
      });
    };

    updateHints();

    const tick = () => {
      const el = dragScrollRef.current;
      const dir = dragScrollDirRef.current;
      if (el && dir) {
        const amount =
          dir.speed * (dir.axis === 'up' ? -1 : 1);
        const prev = el.scrollTop;
        el.scrollTop = prev + amount;
        if (el.scrollTop !== prev) updateHints();
      }
      dragScrollRafRef.current = requestAnimationFrame(tick);
    };
    dragScrollRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (dragScrollRafRef.current) {
        cancelAnimationFrame(dragScrollRafRef.current);
        dragScrollRafRef.current = 0;
      }
    };
  }, [isDragging]);

  const handleDragScrollOver = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const el = dragScrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const max = el.scrollHeight - el.clientHeight;

    let next = null;
    if (y < DRAG_SCROLL_EDGE && el.scrollTop > 0) {
      const depth = 1 - Math.max(0, y) / DRAG_SCROLL_EDGE;
      const speed = DRAG_SCROLL_MIN_PX + depth * (DRAG_SCROLL_MAX_PX - DRAG_SCROLL_MIN_PX);
      next = { axis: 'up', speed };
    } else if (y > rect.height - DRAG_SCROLL_EDGE && el.scrollTop < max) {
      const depth = 1 - Math.max(0, rect.height - y) / DRAG_SCROLL_EDGE;
      const speed = DRAG_SCROLL_MIN_PX + depth * (DRAG_SCROLL_MAX_PX - DRAG_SCROLL_MIN_PX);
      next = { axis: 'down', speed };
    }

    dragScrollDirRef.current = next;
    const label = next?.axis || null;
    setDragScrollDir((prev) => (prev === label ? prev : label));
  };

  const handleDragScrollLeave = (e) => {
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    dragScrollDirRef.current = null;
    setDragScrollDir(null);
  };

  const searchParams = new URLSearchParams(location.search);
  const currentView = searchParams.get('view') || (inboxActive ? 'inbox' : '');
  const currentCategory = searchParams.get('category') || '';

  // Aktiven Ordnerpfad aufklappen
  useEffect(() => {
    if (!currentCategory) return;
    const openAncestors = new Set();
    let parentId = foldersById.get(String(currentCategory))?.parentId ?? null;
    while (parentId != null) {
      openAncestors.add(String(parentId));
      parentId = foldersById.get(String(parentId))?.parentId ?? null;
    }
    if (openAncestors.size === 0) return;
    setCollapsedFolderIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      openAncestors.forEach((id) => {
        if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [currentCategory, foldersById]);

  const closeMobile = () => setSidebarOpen(false);

  const goInbox = (view, extra = {}) => {
    const params = new URLSearchParams();
    if (view && view !== 'inbox') params.set('view', view);
    if (extra.category) params.set('category', String(extra.category));
    const qs = params.toString();
    navigate(qs ? `/inbox?${qs}` : '/inbox');
    closeMobile();
  };

  const markCategoryEntering = (id) => {
    if (id == null) return;
    const key = String(id);
    setEnteringCategoryIds((prev) => new Set(prev).add(key));
    window.setTimeout(() => {
      setEnteringCategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, 360);
  };

  const startCreateCategory = (e, parentId = null) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (parentId != null) {
      const parent = foldersById.get(String(parentId));
      const parentDepth = Number(parent?.depth) || 0;
      if (parentDepth >= MAX_FOLDER_DEPTH) {
        toast.error(`Maximal ${MAX_FOLDER_DEPTH} Unterordner-Ebenen erlaubt`);
        return;
      }
    }
    setInboxOpen(true);
    setCreateParentId(parentId);
    setCreatingCategory(true);
    if (parentId != null) {
      setCollapsedFolderIds((prev) => {
        if (!prev.has(String(parentId))) return prev;
        const next = new Set(prev);
        next.delete(String(parentId));
        return next;
      });
    }
    window.requestAnimationFrame(() => {
      const form = document.querySelector('.category-form-enter');
      form?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };

  const cancelCreateCategory = () => {
    setCreatingCategory(false);
    setCreateParentId(null);
    setNewCategoryName('');
    setNewCategoryColor(CATEGORY_COLORS[0]);
  };

  const renderCreateCategoryForm = (parentFolder = null) => (
    <form
      onSubmit={handleCreateCategory}
      className="px-1 pt-0.5 pb-1.5 space-y-1.5 category-form-enter"
      style={{
        paddingLeft: `${4 + (parentFolder ? ((parentFolder.depth || 0) + 1) * 12 : 0)}px`,
      }}
    >
      {parentFolder ? (
        <p className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate px-0.5">
          Unterordner von „{parentFolder.name}“
        </p>
      ) : (
        <p className="text-[10px] text-neutral-500 dark:text-neutral-400 px-0.5">
          Neuer Ordner
        </p>
      )}
      <input
        autoFocus
        value={newCategoryName}
        onChange={(e) => setNewCategoryName(e.target.value)}
        placeholder="Name…"
        disabled={savingCategory}
        className="w-full text-[12px] px-2 py-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
      />
      <div className="flex flex-wrap items-center gap-1.5 px-0.5">
        {CATEGORY_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            disabled={savingCategory}
            onClick={() => setNewCategoryColor(c)}
            className={`w-5 h-5 rounded-full transition-transform hover:scale-110 disabled:opacity-60 ${
              newCategoryColor === c ? 'ring-2 ring-offset-1 ring-neutral-800 dark:ring-white' : ''
            }`}
            style={{ backgroundColor: c }}
          />
        ))}
        <label className="w-5 h-5 rounded-full border border-dashed border-neutral-300 dark:border-neutral-600 overflow-hidden cursor-pointer relative">
          <input
            type="color"
            value={newCategoryColor}
            disabled={savingCategory}
            onChange={(e) => setNewCategoryColor(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
          <span className="absolute inset-0 flex items-center justify-center text-[9px] text-neutral-400">+</span>
        </label>
      </div>
      <div className="flex gap-1">
        <button
          type="submit"
          disabled={savingCategory || !newCategoryName.trim()}
          className="flex-1 text-[11px] py-1 rounded-md bg-primary-600 text-white disabled:opacity-50"
        >
          {savingCategory ? '…' : 'Speichern'}
        </button>
        <button
          type="button"
          disabled={savingCategory}
          onClick={cancelCreateCategory}
          className="flex-1 text-[11px] py-1 rounded-md border border-neutral-200 dark:border-neutral-700 disabled:opacity-50"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );

  const toggleFolderCollapsed = (folderId, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const key = String(folderId);
    setCollapsedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCreateCategory = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (savingCategoryRef.current) return;
    const name = newCategoryName.trim();
    if (!name) return;

    savingCategoryRef.current = true;
    setSavingCategory(true);
    const parentId = createParentId;
    try {
      const cat = await createCategory(name, newCategoryColor, parentId);
      setNewCategoryName('');
      setNewCategoryColor(CATEGORY_COLORS[0]);
      setCreatingCategory(false);
      setCreateParentId(null);
      markCategoryEntering(cat?.id);
      toast.success(parentId != null ? 'Unterordner erstellt' : 'Ordner erstellt');
      if (cat?.id) goInbox('category', { category: cat.id });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ordner konnte nicht erstellt werden');
    } finally {
      savingCategoryRef.current = false;
      setSavingCategory(false);
    }
  };

  const handleCategoryColor = async (cat, color, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (String(cat.color || '').toLowerCase() === String(color).toLowerCase()) {
      setEditingColorId(null);
      return;
    }
    try {
      await updateCategory(cat.id, { color });
      setEditingColorId(null);
      toast.success('Farbe gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Farbe konnte nicht gespeichert werden');
    }
  };

  const requestDeleteCategory = (cat, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setCategoryToDelete(cat);
  };

  const confirmDeleteCategory = async () => {
    const cat = categoryToDelete;
    if (!cat?.id || deletingCategory) return;
    const key = String(cat.id);
    setCategoryToDelete(null);
    setDeletingCategory(true);
    setExitingCategoryIds((prev) => new Set(prev).add(key));
    setEditingColorId((cur) => (cur === cat.id ? null : cur));

    await new Promise((resolve) => window.setTimeout(resolve, CATEGORY_EXIT_MS));

    try {
      await deleteCategory(cat.id);
      toast.success('Ordner gelöscht');
      const viewingDeleted =
        String(currentCategory) === String(cat.id) ||
        (() => {
          let parentId = foldersById.get(String(currentCategory))?.parentId ?? null;
          while (parentId != null) {
            if (String(parentId) === String(cat.id)) return true;
            parentId = foldersById.get(String(parentId))?.parentId ?? null;
          }
          return false;
        })();
      if (viewingDeleted) goInbox('inbox');
    } catch (err) {
      setExitingCategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      toast.error(err.response?.data?.error || 'Löschen fehlgeschlagen');
    } finally {
      setExitingCategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setDeletingCategory(false);
    }
  };

  const linkClass = (isActive) =>
    `flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
      isActive
        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
    }`;

  const childItemClass = (active) =>
    `w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-[background-color,box-shadow] duration-100 ${
      active
        ? 'bg-primary-100/90 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 shadow-sm'
        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
    } ${inboxOpen ? 'opacity-100' : 'opacity-0'} ${
      iconRail ? 'lg:opacity-100 lg:justify-center lg:px-1.5 lg:gap-0' : ''
    }`;

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-neutral-900/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 h-full max-h-screen min-h-0 bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 flex flex-col overflow-hidden transform transition-[transform,width] duration-300 w-72 ${
          iconRail ? 'lg:w-[4.25rem]' : ''
        } ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isDragging ? 'ring-2 ring-inset ring-primary-400/40' : ''}`}
      >
        <div className={`flex-shrink-0 flex items-center h-[63px] border-b border-neutral-200 dark:border-neutral-800 ${
          iconRail ? 'justify-between px-3 sm:px-4 lg:justify-center lg:px-2' : 'justify-between px-3 sm:px-4'
        }`}>
          <div className={`flex items-center gap-2.5 min-w-0 ${iconRail ? 'lg:justify-center' : ''}`}>
            <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center shadow-glow flex-shrink-0" title="Odium Manager">
              <Mail className="w-4 h-4 text-white" />
            </div>
            <div className={`min-w-0 ${iconRail ? 'lg:hidden' : ''}`}>
              <h1 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-white truncate leading-tight">
                {isDragging ? 'Ablegen in…' : 'Odium Manager'}
              </h1>
              <p className="text-[11px] text-neutral-500 dark:text-neutral-500 truncate leading-tight mt-0.5">
                {isDragging
                  ? (dragging?.count > 1
                    ? `${dragging.count} Nachrichten`
                    : dragging?.preview?.title || dragging?.label || 'E-Mail')
                  : 'Postfach · Kontakte · Planung'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isDragging ? (
          <div className="relative flex-1 min-h-0 flex flex-col">
            {dragScrollHints.up ? (
              <div
                className={`pointer-events-none absolute inset-x-0 top-0 z-20 h-14 flex flex-col items-center justify-center gap-0.5 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-neutral-900 dark:via-neutral-900/95 ${
                  dragScrollDir === 'up' ? 'opacity-100' : 'opacity-80'
                }`}
              >
                <ChevronUp
                  className={`w-4 h-4 ${
                    dragScrollDir === 'up'
                      ? 'text-primary-600 dark:text-primary-400 animate-bounce'
                      : 'text-neutral-400'
                  }`}
                />
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider ${
                    dragScrollDir === 'up'
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-neutral-500'
                  }`}
                >
                  Hier halten · nach oben
                </span>
              </div>
            ) : null}
            {dragScrollHints.down ? (
              <div
                className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 h-14 flex flex-col items-center justify-center gap-0.5 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-neutral-900 dark:via-neutral-900/95 ${
                  dragScrollDir === 'down' ? 'opacity-100' : 'opacity-80'
                }`}
              >
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider ${
                    dragScrollDir === 'down'
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-neutral-500'
                  }`}
                >
                  Hier halten · nach unten
                </span>
                <ChevronDown
                  className={`w-4 h-4 ${
                    dragScrollDir === 'down'
                      ? 'text-primary-600 dark:text-primary-400 animate-bounce'
                      : 'text-neutral-400'
                  }`}
                />
              </div>
            ) : null}
            <nav
              ref={dragScrollRef}
              onDragOver={handleDragScrollOver}
              onDragLeave={handleDragScrollLeave}
              className="flex-1 min-h-0 overflow-y-hidden overscroll-none px-3 py-3 space-y-1"
            >
              <p className="px-1 mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
                Ordner
              </p>
              <SidebarDropZone
                dropKey="drop-inbox"
                target={{ type: 'folder', folder: 'inbox' }}
                label="Empfangen"
                icon={Inbox}
              />
              <SidebarDropZone
                dropKey="drop-important"
                target={{ type: 'important' }}
                label="Wichtig"
                icon={Star}
              />
              <SidebarDropZone
                dropKey="drop-spam"
                target={{ type: 'folder', folder: 'spam' }}
                label="Spam"
                icon={ShieldAlert}
              />
              <SidebarDropZone
                dropKey="drop-trash"
                target={{ type: 'folder', folder: 'trash' }}
                label="Papierkorb"
                icon={Trash2}
              />
              {flatFolders.length > 0 ? (
                <>
                  <div
                    className="mx-1 my-2 border-t border-neutral-200/80 dark:border-neutral-700/80"
                    aria-hidden="true"
                  />
                  <p className="px-1 pt-2 mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
                    Eigene Ordner
                  </p>
                  <div className="space-y-0.5">
                    {flatFolders.map((cat) => {
                      const parent =
                        cat.parentId != null ? foldersById.get(String(cat.parentId)) : null;
                      const hasChildren =
                        Array.isArray(cat.children) && cat.children.length > 0;
                      return (
                        <SidebarDropZone
                          key={cat.id}
                          dropKey={`drop-category:${cat.id}`}
                          target={{ type: 'category', categoryId: cat.id, categoryName: cat.name }}
                          label={cat.name}
                          color={cat.color || '#0f766e'}
                          depth={cat.depth || 0}
                          parentLabel={parent?.name || null}
                          nestedLeaf={cat.parentId != null && !hasChildren}
                        />
                      );
                    })}
                  </div>
                </>
              ) : null}
              {/* Platz unter der Liste, damit die untere Scroll-Zone greifbar bleibt */}
              <div className="h-10" aria-hidden="true" />
            </nav>
          </div>
        ) : (
        <nav className={`flex-1 min-h-0 overflow-y-auto overscroll-contain pt-3 pb-4 space-y-1 ${
          iconRail ? 'px-3 lg:px-1.5' : 'px-3'
        }`}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={closeMobile}
              title={item.label}
              className={({ isActive }) =>
                `${linkClass(isActive)} ${iconRail ? 'lg:justify-center lg:px-2' : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={`w-5 h-5 mr-3 flex-shrink-0 ${iconRail ? 'lg:mr-0' : ''} ${
                      isActive
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300'
                    }`}
                  />
                  <span className={`flex-1 ${iconRail ? 'lg:sr-only' : ''}`}>{item.label}</span>
                  {isActive && (
                    <span className={`ml-auto w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse ${iconRail ? 'lg:hidden' : ''}`} />
                  )}
                </>
              )}
            </NavLink>
          ))}

          <div className={`my-6 border-t border-neutral-200 dark:border-neutral-800 ${iconRail ? 'mx-3 lg:mx-1' : 'mx-3'}`} aria-hidden="true" />

          <div className="pt-0.5">
            <div
              className={`w-full flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                iconRail ? 'lg:hidden' : ''
              } ${
                inboxActive
                  ? 'bg-primary-50/80 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
              }`}
            >
              <button
                type="button"
                onClick={() => setInboxOpen((v) => !v)}
                className="flex items-center min-w-0 flex-1 text-left"
                aria-expanded={inboxOpen}
              >
                <Inbox
                  className={`w-5 h-5 mr-3 transition-transform duration-300 flex-shrink-0 ${
                    inboxOpen ? 'scale-110 text-primary-600 dark:text-primary-400' : 'text-neutral-400'
                  }`}
                />
                <span className="flex-1 truncate">Posteingang</span>
                {unreadCount > 0 && (
                  <span className="mr-1.5 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary-600 text-white text-[11px] font-semibold tabular-nums flex items-center justify-center">
                    {unreadCount > 5000 ? '5000+' : unreadCount}
                  </span>
                )}
              </button>
              {inboxOpen && (
                <button
                  type="button"
                  onClick={(e) => startCreateCategory(e, null)}
                  className="p-1 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-100/80 dark:hover:bg-primary-900/40 transition-colors flex-shrink-0"
                  title="Ordner erstellen"
                  aria-label="Ordner erstellen"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setInboxOpen((v) => !v)}
                className="p-1 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex-shrink-0 ml-0.5"
                aria-label={inboxOpen ? 'Posteingang einklappen' : 'Posteingang ausklappen'}
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                    inboxOpen ? 'rotate-180 text-primary-500' : ''
                  }`}
                />
              </button>
            </div>

            <div
              className={`inbox-submenu grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                inboxOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              } ${iconRail ? 'lg:grid-rows-[1fr]' : ''}`}
            >
              <div className="overflow-hidden min-h-0">
                <div
                  className={`mt-1 mb-1 space-y-0.5 transition-all duration-300 ml-3 pl-3 border-l-2 border-primary-200/80 dark:border-primary-800/60 ${
                    iconRail ? 'lg:ml-0 lg:pl-0 lg:border-l-0' : ''
                  } ${
                    inboxOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
                  } ${iconRail ? 'lg:opacity-100 lg:translate-y-0' : ''}`}
                >
                  {creatingCategory && createParentId == null
                    ? renderCreateCategoryForm(null)
                    : null}

                  {inboxChildren.map((child) => {
                    const active =
                      inboxActive &&
                      currentView === child.view &&
                      !currentCategory;
                    const badgeCount =
                      child.view === 'inbox'
                        ? folderCounts?.inbox
                        : child.view === 'important'
                            ? folderCounts?.important
                            : child.view === 'sent'
                              ? folderCounts?.sent
                              : child.view === 'drafts'
                                ? folderCounts?.drafts
                              : child.view === 'spam'
                                ? folderCounts?.spam
                                : child.view === 'trash'
                                  ? folderCounts?.trash
                                  : 0;
                    const badgeTone =
                      child.view === 'trash'
                        ? 'danger'
                        : child.view === 'spam'
                          ? 'spam'
                          : child.view === 'important'
                            ? 'warning'
                            : child.view === 'sent' || child.view === 'inbox' || child.view === 'drafts'
                              ? 'muted'
                              : 'primary';
                    const alwaysShowBadge =
                      child.view === 'inbox' ||
                      child.view === 'spam' ||
                      child.view === 'trash';
                    const iconTone = active
                      ? 'text-primary-600'
                      : child.view === 'trash'
                        ? 'text-danger-600 dark:text-danger-400'
                        : child.view === 'spam'
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-neutral-400';
                    return (
                      <button
                        key={child.view}
                        type="button"
                        onClick={() => goInbox(child.view)}
                        title={child.label}
                        className={childItemClass(active)}
                      >
                        <span className="relative flex-shrink-0">
                          <child.icon className={`w-4 h-4 ${iconTone}`} />
                          {iconRail && (alwaysShowBadge || Number(badgeCount) > 0) ? (
                            <span
                              className={`hidden lg:flex absolute -top-1.5 -right-2 min-w-[0.9rem] h-3.5 px-0.5 rounded-full text-[9px] font-semibold tabular-nums items-center justify-center leading-none ${
                                badgeTone === 'danger'
                                  ? 'bg-danger-600 text-white'
                                  : badgeTone === 'spam'
                                    ? 'bg-orange-600 text-white'
                                    : 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-100'
                              }`}
                            >
                              {formatFolderBadge(badgeCount, { alwaysShow: true })}
                            </span>
                          ) : null}
                        </span>
                        <span className={`truncate flex-1 text-left ${iconRail ? 'lg:sr-only' : ''}`}>{child.label}</span>
                        <span className={iconRail ? 'lg:hidden contents' : 'contents'}>
                          <FolderCountBadge
                            count={badgeCount}
                            tone={badgeTone}
                            alwaysShow={alwaysShowBadge}
                          />
                        </span>
                      </button>
                    );
                  })}

                  {flatFolders.length > 0 ? (
                    <div
                      className="mx-1 my-1.5 border-t border-neutral-200/80 dark:border-neutral-700/80"
                      aria-hidden="true"
                    />
                  ) : null}

                  {visibleFolders.map((cat) => {
                    const active =
                      inboxActive &&
                      currentView === 'category' &&
                      String(currentCategory) === String(cat.id);
                    const colorOpen = editingColorId === cat.id;
                    const color = cat.color || '#0f766e';
                    const catKey = String(cat.id);
                    const isExiting = exitingCategoryIds.has(catKey);
                    const isEntering = enteringCategoryIds.has(catKey);
                    const hasChildren = Array.isArray(cat.children) && cat.children.length > 0;
                    const isNestedLeaf = cat.parentId != null && !hasChildren;
                    const isCollapsed = collapsedFolderIds.has(catKey);
                    const depth = Number(cat.depth) || 0;
                    return (
                      <div
                        key={cat.id}
                        className={`grid transition-[grid-template-rows,opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isExiting
                            ? 'grid-rows-[0fr] opacity-0 -translate-x-2 pointer-events-none'
                            : 'grid-rows-[1fr] opacity-100 translate-x-0'
                        } ${isEntering ? 'category-enter' : ''}`}
                      >
                        <div className="overflow-hidden min-h-0 relative">
                          <button
                            type="button"
                            onClick={() => goInbox('category', { category: cat.id })}
                            style={{
                              paddingLeft: iconRail ? undefined : `${8 + depth * 12}px`,
                              backgroundColor: active ? withAlpha(color, 0.16) : undefined,
                              boxShadow: active ? `inset 0 0 0 1px ${withAlpha(color, 0.35)}` : undefined,
                              '--cat-hover-bg': withAlpha(color, 0.1),
                            }}
                            title={cat.name}
                            className={`w-full flex items-center gap-1.5 pr-2 py-2 rounded-lg text-[13px] font-medium text-neutral-700 dark:text-neutral-300 transition-[background-color,box-shadow] duration-100 group/cat ${
                              iconRail ? 'lg:justify-center lg:px-1.5 lg:gap-0' : ''
                            } ${
                              active
                                ? ''
                                : 'hover:bg-[var(--cat-hover-bg)]'
                            } ${inboxOpen ? 'opacity-100' : 'opacity-0'} ${
                              iconRail ? 'lg:opacity-100' : ''
                            }`}
                          >
                            {hasChildren ? (
                              <span
                                role="button"
                                tabIndex={0}
                                title={isCollapsed ? 'Aufklappen' : 'Einklappen'}
                                onClick={(e) => toggleFolderCollapsed(cat.id, e)}
                                onKeyDown={(e) => e.key === 'Enter' && toggleFolderCollapsed(cat.id, e)}
                                className={`w-4 h-4 flex items-center justify-center flex-shrink-0 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 ${
                                  iconRail ? 'lg:hidden' : ''
                                }`}
                              >
                                <ChevronRight
                                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                                    isCollapsed ? '' : 'rotate-90'
                                  }`}
                                />
                              </span>
                            ) : (
                              <span className={`w-4 h-4 flex-shrink-0 ${iconRail ? 'lg:hidden' : ''}`} aria-hidden="true" />
                            )}
                            <span
                              role="button"
                              tabIndex={0}
                              title="Farbe ändern"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditingColorId((cur) => (cur === cat.id ? null : cat.id));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setEditingColorId((cur) => (cur === cat.id ? null : cat.id));
                                }
                              }}
                              className="flex-shrink-0 rounded p-0.5 ring-2 ring-offset-1 ring-offset-white dark:ring-offset-neutral-900 ring-transparent hover:ring-neutral-300 dark:hover:ring-neutral-600"
                            >
                              {isNestedLeaf ? (
                                <Mail className="w-4 h-4" style={{ color }} />
                              ) : (
                                <Folder
                                  className="w-4 h-4"
                                  style={{ color }}
                                  fill={color}
                                  fillOpacity={0.22}
                                />
                              )}
                            </span>
                            <span className={`truncate flex-1 text-left ${iconRail ? 'lg:sr-only' : ''}`}>{cat.name}</span>
                            <span className={iconRail ? 'lg:hidden contents' : 'contents'}>
                              <FolderCountBadge count={cat.itemCount} tone="muted" />
                            </span>
                            {depth < MAX_FOLDER_DEPTH ? (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => startCreateCategory(e, cat.id)}
                                onKeyDown={(e) => e.key === 'Enter' && startCreateCategory(e, cat.id)}
                                className={`opacity-0 group-hover/cat:opacity-100 p-0.5 text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 ${
                                  iconRail ? 'lg:hidden' : ''
                                }`}
                                title="Unterordner erstellen"
                              >
                                <Plus className="w-3 h-3" />
                              </span>
                            ) : null}
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => requestDeleteCategory(cat, e)}
                              onKeyDown={(e) => e.key === 'Enter' && requestDeleteCategory(cat, e)}
                              className={`opacity-0 group-hover/cat:opacity-100 p-0.5 text-neutral-400 hover:text-red-500 ${
                                iconRail ? 'lg:hidden' : ''
                              }`}
                              title="Löschen"
                            >
                              <X className="w-3 h-3" />
                            </span>
                          </button>
                          {colorOpen && (
                            <div
                              className="mx-2 mb-1 mt-0.5 p-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 flex flex-wrap gap-1.5 scale-in"
                              style={{ marginLeft: `${8 + depth * 12}px` }}
                            >
                              {CATEGORY_COLORS.map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  title={c}
                                  onClick={(e) => handleCategoryColor(cat, c, e)}
                                  className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${
                                    String(cat.color || '').toLowerCase() === c.toLowerCase()
                                      ? 'ring-2 ring-offset-1 ring-neutral-800 dark:ring-white'
                                      : ''
                                  }`}
                                  style={{ backgroundColor: c }}
                                />
                              ))}
                              <label
                                className="w-5 h-5 rounded-full border border-dashed border-neutral-300 dark:border-neutral-600 overflow-hidden cursor-pointer relative"
                                title="Eigene Farbe"
                              >
                                <input
                                  type="color"
                                  value={cat.color || '#0f766e'}
                                  onChange={(e) => handleCategoryColor(cat, e.target.value, e)}
                                  className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <span className="absolute inset-0 flex items-center justify-center text-[9px] text-neutral-400">+</span>
                              </label>
                            </div>
                          )}
                          {creatingCategory &&
                          createParentId != null &&
                          String(createParentId) === String(cat.id)
                            ? renderCreateCategoryForm(cat)
                            : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </nav>
        )}

        {!isDragging ? (
        <div
          ref={shellFooterRef}
          className="flex-shrink-0 px-3 py-2.5 border-t border-neutral-200 dark:border-neutral-800"
        >
          {mailboxQuota?.supported ? (
            <div
              className={`px-1 pb-2 ${iconRail ? 'lg:hidden' : ''}`}
              title={`Postfach-Speicher: ${formatMailboxBytes(mailboxQuota.usedBytes)} von ${formatMailboxBytes(mailboxQuota.limitBytes)} belegt (${mailboxQuota.percent} %) · ${formatMailboxBytes(mailboxQuota.freeBytes)} frei`}
            >
              <div className="flex items-center justify-between gap-2 text-[10px] text-neutral-500 dark:text-neutral-400">
                <span className="truncate">Speicher</span>
                <span
                  className={`tabular-nums flex-shrink-0 ${
                    mailboxQuota.percent >= 95
                      ? 'text-danger-600 dark:text-danger-400 font-semibold'
                      : mailboxQuota.percent >= 85
                        ? 'text-amber-600 dark:text-amber-400 font-medium'
                        : ''
                  }`}
                >
                  {mailboxQuota.percent} %
                </span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-neutral-200/90 dark:bg-neutral-800 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-[width] duration-500 ${
                    mailboxQuota.percent >= 95
                      ? 'bg-danger-500'
                      : mailboxQuota.percent >= 85
                        ? 'bg-amber-500'
                        : 'bg-primary-500'
                  }`}
                  style={{ width: `${Math.min(100, Math.max(0, Number(mailboxQuota.percent) || 0))}%` }}
                />
              </div>
              <p className="mt-1 text-[10px] text-neutral-400 dark:text-neutral-500 tabular-nums truncate">
                {formatMailboxBytes(mailboxQuota.usedBytes)} / {formatMailboxBytes(mailboxQuota.limitBytes)}
              </p>
            </div>
          ) : null}
          {user?.email ? (
            <p className={`px-1 pb-1.5 text-[10px] text-neutral-400 truncate ${iconRail ? 'lg:hidden' : ''}`} title={user.email}>
              {user.email}
            </p>
          ) : null}
          <div className={`flex items-center gap-0.5 ${iconRail ? 'lg:flex-col' : ''}`}>
            <NavLink
              to="/settings"
              onClick={closeMobile}
              title="Einstellungen"
              className={({ isActive }) =>
                `flex-1 inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[10px] font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50/80 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Settings className={`w-4 h-4 ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}`} />
                  <span className={iconRail ? 'lg:sr-only' : ''}>Einstellungen</span>
                </>
              )}
            </NavLink>
            <button
              type="button"
              title="Abmelden"
              onClick={logout}
              className="flex-1 inline-flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[10px] font-medium text-neutral-500 dark:text-neutral-400 hover:bg-danger-50 dark:hover:bg-danger-900/30 hover:text-danger-600 dark:hover:text-danger-400 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className={iconRail ? 'lg:sr-only' : ''}>Abmelden</span>
            </button>
          </div>
        </div>
        ) : (
          <div className="flex-shrink-0 px-4 py-3 border-t border-neutral-100 dark:border-neutral-800 text-[11px] text-neutral-500 dark:text-neutral-400">
            Loslassen zum Abbrechen · auf Ziel ablegen
          </div>
        )}
      </aside>

      {categoryToDelete && createPortal(
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => !deletingCategory && setCategoryToDelete(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Ordner löschen?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    Möchtest du den Ordner{' '}
                    <span className="font-medium text-neutral-900 dark:text-neutral-100">
                      „{categoryToDelete.name}“
                    </span>
                    {Array.isArray(categoryToDelete.children) && categoryToDelete.children.length > 0
                      ? ' inklusive aller Unterordner'
                      : ''}{' '}
                    wirklich löschen? Die Zuordnungen der E-Mails gehen dabei verloren.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                disabled={deletingCategory}
                onClick={confirmDeleteCategory}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
              >
                {deletingCategory ? 'Löschen…' : 'Löschen'}
              </button>
              <button
                type="button"
                disabled={deletingCategory}
                onClick={() => setCategoryToDelete(null)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Sidebar;
